import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Optional, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';

import {
  UiPlatformActionTickerBackground,
  UiPlatformActionTickerIcon,
  UiPlatformActionTickerRole,
  UiPlatformActionTickerState
} from './models/action-ticker.model';

import { UiPlatformButtonType } from '../button/button.model';

@Component({
  selector: 'ui-platform-action-ticker',
  templateUrl: './action-ticker.component.html',
  styleUrl: './action-ticker.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ActionTickerComponent implements OnChanges {
  @Input() role!: UiPlatformActionTickerRole;
  @Input() state!: UiPlatformActionTickerState;
  @Optional() @Input() verificationValidationDate!: Date;

  @Output() clicked = new EventEmitter<boolean>();

  public get buttonText(): string {
    if (this.role === 'client' && (this.state === 'activated' || this.state === 'in_progress' || this.state === 'expired')) {
      return `ui-platform.action-ticker.button.client`;
    } else if (this.role === 'verifier' && (this.state === 'in_progress' || this.state === 'verify')) {
      return `ui-platform.action-ticker.button.verifier`;
    } else {
      return '';
    }
  }

  public get description(): string {
    return `ui-platform.action-ticker.description.${this.role}.${this.state}`;
  }

  private _buttonDisabled = false;

  public get buttonDisabled(): boolean {
    return this._buttonDisabled;
  }

  private _buttonType: UiPlatformButtonType = 'primary';

  public get buttonType(): UiPlatformButtonType {
    return this._buttonType;
  }

  private _iconType: UiPlatformActionTickerIcon | undefined = undefined;

  public get iconType(): UiPlatformActionTickerIcon | undefined {
    return this._iconType;
  }

  public get canShowActionTicker(): boolean {
    return !!(this.role && this.state);
  }

  private _backgroundColorClass: UiPlatformActionTickerBackground = 'action-ticker-grey';

  public get backgroundColorClass(): UiPlatformActionTickerBackground {
    return this._backgroundColorClass;
  }

  public get iconColorClass(): string {
    if (!this._iconType?.color) {
      return '';
    }
    return this._iconType.color === 'green' ? 'icon-button-green' : 'icon-button-red';
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.role?.currentValue || !changes?.state?.currentValue) {
      return;
    }

    if (changes?.role?.currentValue === 'client') {
      this._setStatesForClient(this.state);
    } else {
      this._setStatesForVerifier(this.state);
    }
  }

  private _setStatesForClient(state: UiPlatformActionTickerState): void {
    switch (state) {
      case 'in_progress':
        this._backgroundColorClass = 'action-ticker-grey';
        this._buttonDisabled = true;
        break;
      case 'activated':
        this._backgroundColorClass = 'action-ticker-blue';
        break;
      case 'locked':
        this._iconType = {
          type: 'check'
        };
        this._backgroundColorClass = 'action-ticker-blue';
        this._buttonDisabled = true;
        break;
      case 'verified':
        this._iconType = {
          type: 'check',
          color: 'green'
        };
        this._backgroundColorClass = 'action-ticker-green';
        this._buttonDisabled = true;
        break;
      case 'expired':
        this._backgroundColorClass = 'action-ticker-red';
        this._buttonType = 'warn';
        break;
      case 'legacy':
        this._backgroundColorClass = 'action-ticker-dark-grey';
        break;
      default:
        break;
    }
  }

  private _setStatesForVerifier(state: UiPlatformActionTickerState): void {
    switch (state) {
      case 'in_progress':
        this._backgroundColorClass = 'action-ticker-grey';
        this._buttonDisabled = true;
        break;
      case 'waiting':
        this._backgroundColorClass = 'action-ticker-dark-grey';
        break;
      case 'verify':
        this._backgroundColorClass = 'action-ticker-blue';
        break;
      case 'verified':
        this._iconType = {
          type: 'check',
          color: 'green'
        };
        this._backgroundColorClass = 'action-ticker-green';
        this._buttonDisabled = true;
        break;
      case 'expired':
        this._iconType = {
          type: 'checkmark',
          color: 'red'
        };
        this._backgroundColorClass = 'action-ticker-red';
        this._buttonDisabled = true;
        break;
      default:
        break;
    }
  }

  protected click(link: boolean): void {
    this.clicked.emit(link);
  }
}
